/**
 * i18n — Arabic + English strings for the app.
 * Default is Arabic; English used when advisor toggles in Step 1.
 */

import { useSessionStore } from '@stores/sessionStore';

type StringMap = Record<string, { ar: string; en: string }>;

const strings: StringMap = {
  // Common
  'common.next': { ar: 'التالي ←', en: 'Next →' },
  'common.back': { ar: '→ رجوع', en: '← Back' },
  'common.start': { ar: 'ابدأ', en: 'Start' },
  'common.continue': { ar: 'متابعة', en: 'Continue' },
  'common.save': { ar: 'حفظ', en: 'Save' },
  'common.cancel':  { ar: 'إلغاء', en: 'Cancel' },
  'common.confirm': { ar: 'تأكيد',  en: 'Confirm' },

  // V3 Day 3.0 (2026-05-08) — New Customer reset
  'common.newCustomer':                { ar: 'عميل جديد',                   en: 'New customer' },
  'common.newCustomerConfirmTitle':    { ar: 'بدء جلسة جديدة؟',             en: 'Start a new session?' },
  'common.newCustomerConfirmMessage':  { ar: 'سيتم فقدان البيانات الحالية.', en: 'The current session data will be lost.' },
  'common.optional': { ar: 'اختياري', en: 'Optional' },
  'common.required': { ar: 'مطلوب', en: 'Required' },
  'common.omr': { ar: 'ر.ع', en: 'OMR' },
  'common.year': { ar: 'سنة', en: 'year' },
  'common.month': { ar: 'شهر', en: 'month' },
  'common.minute': { ar: 'دقيقة', en: 'min' },

  // Step 1 — Welcome
  'welcome.title': { ar: 'بوابتك للاستثمار الذكي', en: 'Your gateway to smart investment' },
  'welcome.subtitle': {
    ar: 'جلسة استشارية لمدة ٢٠–٢٥ دقيقة لاختيار وحدتك في KEY2',
    en: 'A 20–25 minute advisory session to choose your unit in KEY2',
  },
  'welcome.clientName': { ar: 'اسم العميل', en: 'Client name' },
  'welcome.clientNamePh': { ar: 'مثال: أحمد العامري', en: 'e.g. Ahmed Al-Amri' },
  'welcome.phone': { ar: 'رقم الهاتف', en: 'Phone number' },
  'welcome.phonePh': { ar: '+968XXXXXXXX أو ٨ أرقام', en: '+968XXXXXXXX or 8 digits' },
  'welcome.phoneHint': {
    ar: 'رقم عماني: +968 ثم ٨ أرقام، أو ٨ أرقام محلية',
    en: 'Oman number: +968 then 8 digits, or 8 local digits',
  },
  'welcome.email': { ar: 'البريد الإلكتروني', en: 'Email' },
  'welcome.emailPh': { ar: 'name@example.com', en: 'name@example.com' },
  'welcome.advisor': { ar: 'المستشار', en: 'Advisor' },
  'welcome.advisorPh': { ar: 'اختر المستشار…', en: 'Select advisor…' },
  'welcome.startBtn': { ar: 'ابدأ الجلسة', en: 'Start Session' },
  'welcome.languageLabel': { ar: 'اللغة', en: 'Language' },
  'welcome.langAr': { ar: 'العربية', en: 'Arabic' },
  'welcome.langEn': { ar: 'الإنجليزية', en: 'English' },
  'welcome.errClientName': { ar: 'الرجاء إدخال اسم العميل', en: 'Please enter the client name' },
  'welcome.errPhone': { ar: 'رقم هاتف غير صحيح', en: 'Invalid phone number' },
  'welcome.errEmail': { ar: 'بريد إلكتروني غير صحيح', en: 'Invalid email address' },
  'welcome.errAdvisor': { ar: 'الرجاء اختيار المستشار', en: 'Please select an advisor' },
  'welcome.timeBudget': { ar: 'الوقت المتوقع للخطوة', en: 'Estimated step time' },

  // Step 2 — Project
  'project.tagline': { ar: 'معيار جديد للاستثمار في الخوض', en: 'A new standard for investment in Al Khoud' },
  'project.exploreLocation': { ar: 'استكشف الموقع', en: 'Explore location' },
  'project.exploreProject': { ar: 'استكشف المشروع', en: 'Explore project' },
  'project.units': { ar: 'وحدة', en: 'units' },
  'project.floors': { ar: 'طوابق', en: 'floors' },
  'project.shops': { ar: 'محل', en: 'shops' },
  'project.startsFrom': { ar: 'يبدأ من', en: 'starts from' },
  'project.delivery': { ar: 'التسليم', en: 'Delivery' },
  'project.deliveryLabel': { ar: 'تسليم', en: 'Delivery' },
  'project.mapTitle': { ar: 'الموقع ومحاور الطلب', en: 'Location & demand anchors' },
  'project.mapAriaLabel': { ar: 'خريطة موقع KEY2 ومحيطه', en: 'Map of KEY2 location and surroundings' },
  'project.galleryTitle': { ar: 'المرافق الرئيسية', en: 'Key amenities' },
  'project.imgPool': { ar: 'مسبح', en: 'Pool' },
  'project.imgLobby': { ar: 'البهو', en: 'Lobby' },
  'project.imgGym': { ar: 'صالة رياضية', en: 'Gym' },
  'project.imgParking': { ar: 'مواقف', en: 'Parking' },
  'project.demandTitle': { ar: 'لماذا الخوض؟', en: 'Why Al Khoud?' },
  'project.anchorsLabel': { ar: 'محاور قريبة', en: 'nearby anchors' },
  'project.timeBudget': { ar: 'الوقت المتوقع للخطوة', en: 'Estimated step time' },
  'project.deliveryMar2029': { ar: 'مارس ٢٠٢٩', en: 'March 2029' },

  // Step 3 — Goal
  'goal.title': { ar: 'هدفك من الاستثمار؟', en: 'Your investment goal?' },
  'goal.subtitle': {
    ar: 'ثلاثة أسئلة سريعة، نعرض لك بعدها أنسب الوحدات',
    en: 'Three quick questions, then we show your best matches',
  },
  'goal.timeBudget': { ar: 'الوقت المتوقع للخطوة', en: 'Estimated step time' },
  'goal.q1': { ar: 'هدفك من الشراء؟', en: 'Your purchase goal?' },
  'goal.q1_hint': { ar: 'اختر واحد', en: 'Pick one' },
  'goal.investment': { ar: 'استثمار', en: 'Investment' },
  'goal.investment_desc': { ar: 'تأجير أو نمو رأسمالي', en: 'Rental or capital growth' },
  'goal.living': { ar: 'سكن شخصي', en: 'Personal living' },
  'goal.living_desc': { ar: 'لي ولعائلتي', en: 'For me and my family' },
  'goal.both': { ar: 'الاثنين معاً', en: 'Both' },
  'goal.both_desc': { ar: 'سكن الآن، استثمار لاحقاً', en: 'Live now, invest later' },
  'goal.q2': { ar: 'ميزانيتك؟', en: 'Your budget?' },
  'goal.q2_hint': { ar: 'بالريال العماني، شامل القيمة المضافة', en: 'Omani Rials, VAT included' },
  'goal.budget.under25': { ar: 'أقل من ٢٥ ألف', en: 'Under 25K' },
  'goal.budget.25_40': { ar: '٢٥ – ٤٠ ألف', en: '25 – 40K' },
  'goal.budget.40_50': { ar: '٤٠ – ٥٠ ألف', en: '40 – 50K' },
  'goal.budget.over50': { ar: 'أكثر من ٥٠ ألف', en: '50K+' },
  'goal.q3': { ar: 'كم غرفة؟', en: 'How many bedrooms?' },
  'goal.q3_hint': { ar: 'عدد غرف النوم المطلوبة', en: 'Bedrooms needed' },
  'goal.beds.1': { ar: 'غرفة واحدة', en: '1 bedroom' },
  'goal.beds.2': { ar: 'غرفتان', en: '2 bedrooms' },
  'goal.beds.3': { ar: 'ثلاث غرف', en: '3 bedrooms' },
  'goal.beds.flex': { ar: 'مرن', en: 'Flexible' },
  'goal.flex': { ar: 'مرن', en: 'Flexible' },
  'goal.q4': { ar: 'الأولوية الاستثمارية؟', en: 'Your investment priority?' },
  'goal.q4_hint': { ar: 'يساعدنا في ترتيب التوصيات', en: 'Helps us rank recommendations' },
  'goal.priority.rental': { ar: 'عائد إيجاري', en: 'Rental yield' },
  'goal.priority.rental_desc': { ar: 'دخل شهري ثابت', en: 'Steady monthly income' },
  'goal.priority.capital': { ar: 'نمو رأسمالي', en: 'Capital growth' },
  'goal.priority.capital_desc': { ar: 'ارتفاع قيمة الأصل', en: 'Asset appreciation' },
  'goal.priority.both': { ar: 'الاثنين معاً', en: 'Both' },
  'goal.priority.both_desc': { ar: 'توازن بين الدخل والنمو', en: 'Balance income and growth' },
  'goal.foundUnits': { ar: 'وجدنا', en: 'We found' },
  'goal.foundUnitsSuffix': { ar: 'وحدة تناسبك', en: 'units that match' },
  'goal.noUnits': {
    ar: 'لا توجد وحدات مطابقة — جرّب توسيع الميزانية أو غرف مرنة',
    en: 'No matching units — try a wider budget or flexible bedrooms',
  },
  'goal.fallbackTitle': { ar: 'ربما هذا أفضل لك', en: 'You might prefer this' },
  'goal.fallbackYes': { ar: 'نعم، جرّب', en: 'Yes, try it' },
  'goal.fallbackNo': { ar: 'لا، أرجع للسؤال', en: 'No, change my answer' },
  'goal.advisorTitle': {
    ar: 'خيارات محدودة بهذي المعايير',
    en: 'Limited options with these criteria',
  },
  'goal.advisorBody': {
    ar: 'مستشارك راح يساعدك على إيجاد الأنسب — تواصل مباشر يفتح خيارات إضافية.',
    en: 'Your advisor can help you find the best fit — a direct conversation often opens up additional options.',
  },
  'goal.advisorBtn': { ar: 'تواصل مع المستشار', en: 'Talk to advisor' },
  'goal.advisorAlert': {
    ar: 'سيتم تفعيل التواصل عبر واتساب في الخطوة ٨ — سجل ملاحظة للمستشار الحالي.',
    en: 'WhatsApp handoff is wired up in Step 8 — please make a note for the advisor.',
  },

  // Step 4 — Match (Browse phase)
  'match.title': { ar: 'وجدنا', en: 'We found' },
  'match.titleSuffix': { ar: 'وحدة تناسبك', en: 'units that match you' },
  'match.subtitle': {
    ar: 'اخترنا الأفضل بناءً على إطلالتها وموقعها في المبنى',
    en: 'Sorted by view rank and floor',
  },
  'match.timeBudget': { ar: 'الوقت المتوقع للخطوة', en: 'Estimated step time' },
  'match.compareMore': { ar: 'قارن أخرى', en: 'Compare more' },
  'match.compareMoreCount': { ar: 'وحدة إضافية', en: 'more units' },
  'match.hideMore': { ar: 'إخفاء البقية', en: 'Hide others' },
  'match.viewLabel': { ar: 'الإطلالة', en: 'View' },
  'match.paybackLabel': { ar: 'استرداد', en: 'Payback' },
  'match.paybackYears': { ar: 'سنة', en: 'yrs' },
  'match.areaUnit': { ar: 'م²', en: 'm²' },
  'match.continueToConfig': {
    ar: 'متابعة إلى الأقساط ←',
    en: 'Continue to installments →',
  },
  'match.chooseThis': { ar: 'اختر هذه الوحدة', en: 'Choose this unit' },
  'match.selectedBadge': { ar: 'مختارة', en: 'Selected' },
  'match.unitCode': { ar: 'كود الوحدة', en: 'Unit code' },
  'match.crumbWelcome': { ar: 'الافتتاح', en: 'Welcome' },
  'match.crumbProject': { ar: 'المشروع', en: 'Project' },
  'match.crumbGoal': { ar: 'الهدف', en: 'Goal' },
  'match.crumbMatch': { ar: 'التوافق', en: 'Match' },
  'match.imgUnavailable': {
    ar: 'مخطط الوحدة غير متاح حالياً',
    en: 'Floor plan unavailable',
  },
  'match.floorPlanAlt': { ar: 'مخطط الوحدة', en: 'Unit floor plan' },

  // Step 4 — Configure phase
  'match.configureTitle': { ar: 'الأرقام بالتفصيل', en: 'Numbers in detail' },
  'match.cash': { ar: 'نقداً', en: 'Cash' },
  'match.monthly': { ar: 'أقساط شهرية', en: 'Monthly' },
  'match.quarterly': { ar: 'أقساط ربع سنوية', en: 'Quarterly' },
  'match.flex': { ar: 'خطة مرنة', en: 'Flex Plan' },
  'match.cashSavingHint': { ar: 'خصم ٣٪ — توفير', en: '3% discount — savings' },
  'match.monthlyHint': { ar: 'دفعة أولى + ٤٠ شهر', en: 'down payment + 40 months' },
  'match.quarterlyHint': { ar: 'دفعة أولى + ١٣ ربع', en: 'down payment + 13 quarters' },
  'match.flexInitialHint': { ar: '/شهر · أول ٣ شهور', en: '/mo · first 3 months' },
  'match.flexMidHint': { ar: 'ثم', en: 'then' },
  'match.flexAfter': { ar: '/شهر بعدها', en: '/mo after that' },
  'match.flexScheduleM1to3': { ar: 'شهر ١-٣', en: 'Month 1-3' },
  'match.flexScheduleM4to39': { ar: 'شهر ٤-٣٩', en: 'Month 4-39' },
  'match.flexScheduleM40': { ar: 'شهر ٤٠ (التسوية)', en: 'Month 40 (settlement)' },
  'match.flexScheduleTitle': {
    ar: 'جدول الخطة المرنة',
    en: 'Flex schedule breakdown',
  },
  'match.perMonth': { ar: '/شهر', en: '/mo' },
  'match.perQuarter': { ar: '/ربع', en: '/qtr' },
  'match.expectedRent': { ar: 'إيجار شهري متوقع', en: 'Expected monthly rent' },
  'match.annualGross': { ar: 'إيجار سنوي إجمالي', en: 'Annual gross' },
  'match.annualNet': { ar: 'إيجار سنوي صافي', en: 'Annual net' },
  'match.grossYield': { ar: 'العائد الإجمالي', en: 'Gross yield' },
  'match.netYield': { ar: 'العائد الصافي', en: 'Net yield' },
  'match.paybackYearsLabel': { ar: 'فترة الاسترداد', en: 'Payback period' },
  'match.years': { ar: 'سنة', en: 'years' },
  'match.assumptions': {
    ar: 'الافتراضات: شغور ٥٪، خدمات ٣.٥ ر.ع/م²/سنة، إدارة ١٠٪',
    en: 'Assumptions: 5% vacancy, OMR 3.5/sqm/yr service charge, 10% management fee',
  },
  'match.summaryFor': { ar: 'ملخص الصفقة', en: 'Deal summary' },
  'match.priceFinal': { ar: 'السعر النهائي', en: 'Final price' },
  'match.priceWithVat': { ar: 'السعر شامل القيمة المضافة', en: 'Price incl. VAT' },
  'match.downPayment': { ar: 'الدفعة الأولى', en: 'Down payment' },
  'match.installment': { ar: 'القسط', en: 'Installment' },
  'match.monthlyShort': { ar: 'القسط الشهري', en: 'Monthly inst.' },
  'match.quarterlyShort': { ar: 'القسط الربعي', en: 'Quarterly inst.' },
  'match.flexFirstMonths': { ar: 'أول ٣ شهور', en: 'First 3 months' },
  'match.flexAfterMonths': { ar: 'بعدها', en: 'Then' },
  'match.continueToGuarantees': {
    ar: 'متابعة إلى الضمانات ←',
    en: 'Continue to guarantees →',
  },
  'match.chooseAnother': { ar: 'اختر وحدة أخرى', en: 'Choose another unit' },
  'match.tooltipVat': {
    ar: 'القيمة المضافة ٥٪ من السعر الأساسي',
    en: 'VAT = 5% of base price',
  },
  'match.tooltipCash': {
    ar: 'خصم ٣٪ من السعر الأساسي عند الدفع نقداً',
    en: '3% off base price when paying cash',
  },
  'match.tooltipMonthly': {
    ar: '(السعر شامل القيمة − الدفعة الأولى) ÷ ٤٠ شهر',
    en: '(price incl. VAT − down payment) ÷ 40 months',
  },
  'match.tooltipQuarterly': {
    ar: '(السعر شامل القيمة − الدفعة الأولى) ÷ ١٣ ربع',
    en: '(price incl. VAT − down payment) ÷ 13 quarters',
  },
  'match.tooltipNetYield': {
    ar: 'الإيجار السنوي الصافي ÷ السعر شامل القيمة',
    en: 'Annual net rent ÷ price incl. VAT',
  },
  'match.tooltipPayback': {
    ar: 'السعر شامل القيمة ÷ الإيجار السنوي الصافي',
    en: 'Price incl. VAT ÷ annual net rent',
  },

  // Step 4 — Match
  'match.candidates': { ar: 'وحدات مرشحة لك', en: 'Recommended units' },
  'match.area': { ar: 'المساحة', en: 'Area' },
  'match.beds': { ar: 'غرف', en: 'beds' },
  'match.baths': { ar: 'حمامات', en: 'baths' },
  'match.balconies': { ar: 'بلكونات', en: 'balconies' },
  'match.view': { ar: 'الإطلالة', en: 'View' },
  'match.payback': { ar: 'فترة الاسترداد', en: 'Payback' },
  'match.choose': { ar: 'احفظ هذه الوحدة', en: 'Save this unit' },
  'match.compareOthers': { ar: 'قارن أخرى', en: 'Compare others' },
  'match.paymentPlan': { ar: 'خطة الدفع', en: 'Payment plan' },
  'match.flexPlan': { ar: 'مرن', en: 'Flex' },
  'match.totalPrice': { ar: 'السعر الإجمالي', en: 'Total price' },
  'match.cashSaving': { ar: 'الخصم النقدي', en: 'Cash discount' },

  // Step 5 — Guarantees
  'guar.title': { ar: 'ضماناتك في كل خطوة', en: 'Your guarantees at every step' },
  'guar.subtitle': {
    ar: 'حقوقك محفوظة بضمانات قانونية واضحة',
    en: 'Your rights protected by clear legal guarantees',
  },
  'guar.timeBudget': { ar: 'الوقت المتوقع للخطوة', en: 'Estimated step time' },
  'guar.crumb': { ar: 'الضمانات', en: 'Guarantees' },
  'guar.delayProtectionNote': {
    ar: 'بالإضافة: حماية من التأخير — غرامة ٠.٥٪/شهر بعد فترة السماح، استرداد كامل بعد سنة',
    en: 'Plus: delay protection — 0.5%/month penalty after the grace period, full refund after one year',
  },
  'guar.continueToReport': {
    ar: 'متابعة إلى التقرير ←',
    en: 'Continue to report →',
  },

  // Step 6 — Report
  'report.title': { ar: 'التقرير الاستثماري الشامل', en: 'Comprehensive Investment Report' },
  'report.shareToClient': { ar: 'إرسال للعميل', en: 'Send to client' },
  'report.shareTooltip': {
    ar: 'يرسل التقرير عبر واتساب ويعرض رمز QR للوصول الفوري',
    en: 'Sends the report via WhatsApp and shows a QR for instant access',
  },
  'report.printSelf': { ar: 'طباعة', en: 'Print' },
  'report.printSelfTooltip': {
    ar: 'طباعة التقرير لاستخدام المستشار',
    en: "Print the report for the advisor's reference",
  },
  'share.title': { ar: 'إرسال التقرير', en: 'Send report' },
  'share.loading': {
    ar: 'جاري إرسال التقرير للعميل…',
    en: 'Sending the report to the client…',
  },
  'share.successTitle': { ar: 'تم الإرسال', en: 'Sent successfully' },
  'share.sentTo': {
    ar: 'تم إرسال التقرير إلى {phone} عبر واتساب',
    en: 'The report was sent to {phone} via WhatsApp',
  },
  'share.scanQr': {
    ar: 'أو امسح الرمز للوصول الفوري',
    en: 'Or scan the code for instant access',
  },
  'share.errorTitle': { ar: 'تعذّر الإرسال', en: 'Sending failed' },
  'share.errorBody': {
    ar: 'حدث خطأ أثناء الإرسال — تحقق من الاتصال وحاول مرة أخرى.',
    en: 'Something went wrong while sending — check the connection and try again.',
  },
  'share.retry': { ar: 'إعادة المحاولة', en: 'Try again' },
  'share.close': { ar: 'إغلاق', en: 'Close' },
  'share.newSession': { ar: 'جلسة جديدة', en: 'New session' },
  'share.devBackend': {
    ar: 'تخزين تجريبي محلي — يتم الترقية للإنتاج لاحقاً',
    en: 'Dev-only in-memory storage — production KV lands later',
  },
  // B.4 (2026-05-08) — assignee feedback after the share completes.
  'share.advisorAssigned': {
    ar: 'تم تعيين العميل لـ {advisor}',
    en: 'Lead assigned to {advisor}',
  },
  'share.advisorAssignFailed': {
    ar: 'تم إرسال الرسالة لكن لم يتم تعيين المستشار تلقائياً — يمكن للمستشار قبول المحادثة من respond.io',
    en: 'Message delivered but auto-assignment failed — the advisor can claim the conversation manually from respond.io',
  },

  // Shared report (/r/:sessionId — QR landing page)
  'shared.title': { ar: 'تقريرك من KEY2', en: 'Your KEY2 report' },
  'shared.loading': { ar: 'جاري تحميل تقريرك…', en: 'Loading your report…' },
  'shared.notFoundTitle': {
    ar: 'هذا الرابط غير صالح أو منتهي الصلاحية',
    en: 'This link is invalid or has expired',
  },
  'shared.errorTitle': { ar: 'تعذّر تحميل التقرير', en: 'Could not load the report' },
  'shared.contactAdvisor': {
    ar: 'للحصول على نسخة جديدة، تواصل مع المستشار الذي أرسل لك الرابط.',
    en: 'For a fresh copy, contact the advisor who sent you the link.',
  },

  // V3 Day 1 (2026-05-08) — Virtual Tour modal (Pannellum)
  'vr.button': { ar: '🥽 جولة افتراضية', en: '🥽 Virtual Tour' },
  'vr.title': { ar: 'الجولة الافتراضية', en: 'Virtual Tour' },
  'vr.loading': { ar: 'جاري تحميل الجولة…', en: 'Loading tour…' },
  'vr.error': {
    ar: 'تعذّر تحميل الجولة — تحقق من الاتصال وحاول مرة أخرى.',
    en: 'Could not load the tour — check connection and try again.',
  },
  'vr.autoRotate': { ar: 'دوران تلقائي', en: 'Auto-rotate' },
  'vr.fullscreen': { ar: 'ملء الشاشة', en: 'Fullscreen' },
  'vr.close': { ar: 'إغلاق', en: 'Close' },
  'vr.unavailable': {
    ar: 'لا تتوفر جولة افتراضية لهذا النوع من الوحدات.',
    en: 'Virtual tour not available for this unit type.',
  },
  'vr.rooms.living': { ar: 'الصالة', en: 'Living Room' },
  'vr.rooms.kitchen': { ar: 'المطبخ', en: 'Kitchen' },
  'vr.rooms.master-bedroom': { ar: 'غرفة النوم الرئيسية', en: 'Master Bedroom' },
  'vr.rooms.bedroom1': { ar: 'غرفة النوم ١', en: 'Bedroom 1' },
  'vr.rooms.bedroom2': { ar: 'غرفة النوم ٢', en: 'Bedroom 2' },
  'vr.rooms.master-bath': { ar: 'حمام رئيسي', en: 'Master Bath' },
  'vr.rooms.passage-bath': { ar: 'حمام عام', en: 'Common Bath' },
  'vr.rooms.living-bath': { ar: 'حمام الصالة', en: 'Living Bath' },
  'vr.rooms.laundry': { ar: 'غرفة الغسيل', en: 'Laundry Room' },
  'report.saveToCrm': { ar: 'حفظ في respond.io', en: 'Save to respond.io' },
  'report.reserve': { ar: 'حجز الوحدة', en: 'Reserve unit' },
  'report.newSession': { ar: 'جلسة جديدة', en: 'New session' },
  'report.fitScore': { ar: 'درجة ملاءمة الصفقة', en: 'Investment fit score' },
  'report.client': { ar: 'العميل', en: 'Client' },
  'report.advisor': { ar: 'المستشار', en: 'Advisor' },
  'report.date': { ar: 'التاريخ', en: 'Date' },
  'report.sessionId': { ar: 'رقم الجلسة', en: 'Session ID' },
  'report.appendix': { ar: 'ملحق', en: 'Appendix' },
  'report.placeholderCrm': {
    ar: 'سيتم تفعيل التكامل مع respond.io في الخطوة ٩',
    en: 'respond.io integration is wired up in M9',
  },
  'report.placeholderReserved': {
    ar: 'تم حجز الوحدة بنجاح! سيتواصل معك المستشار لإكمال إجراءات الحجز.',
    en: 'Unit reserved successfully! Your advisor will reach out to complete the booking process.',
  },
  'report.s1Title': { ar: 'قوة المشروع والموقع', en: 'Project strength & location' },
  'report.s1Subtitle': {
    ar: 'KEY2 في قلب ٣ أحزمة طلب: تعليمي، تقني، طبي',
    en: 'KEY2 sits at the meeting point of 3 demand belts: education, tech, healthcare',
  },
  'report.s2Title': { ar: 'الأرقام بالتفصيل', en: 'Numbers in detail' },
  'report.s2Subtitle': { ar: 'الوحدة المختارة وخطة الدفع', en: 'Selected unit and payment plan' },
  'report.s3Title': { ar: 'الضمانات والحماية', en: 'Guarantees & protection' },
  'report.s3Subtitle': {
    ar: 'حقوقك محفوظة بضمانات قانونية واضحة',
    en: 'Your rights protected by clear legal guarantees',
  },
  'report.s4Title': { ar: 'لماذا هذه الوحدة تناسبك', en: 'Why this unit fits you' },
  'report.s4Subtitle': {
    ar: 'توصية مبنية على إجاباتك والأرقام الفعلية',
    en: 'Recommendation based on your answers and the actual numbers',
  },
  'report.s5Title': { ar: 'مقارنة السوق', en: 'Market comparison' },
  'report.s5Subtitle': {
    ar: 'وحدتك مقابل سوق الخوض السكني',
    en: 'Your unit versus the Al Khoud residential market',
  },
  'report.s6Title': { ar: 'المواقع المرجعية', en: 'Location anchors' },
  'report.s6Subtitle': { ar: 'محاور الطلب حول المشروع', en: 'Demand anchors around the project' },
  'report.s7Title': { ar: 'مرافق المشروع', en: 'Project amenities' },
  'report.s7Subtitle': {
    ar: '١٨ مرفق متكامل لتجربة سكنية مميزة',
    en: '18 integrated amenities for a premium living experience',
  },
  'report.s8Title': { ar: 'جميع الوحدات المرشحة', en: 'All shortlisted units' },
  'report.s8Subtitle': {
    ar: 'الجدول الكامل بالأسعار والأقساط والعائد',
    en: 'Full table with prices, installments, and yield',
  },
  'report.basePrice': { ar: 'السعر الأساسي', en: 'Base price' },
  'report.vat': { ar: 'القيمة المضافة (٥٪)', en: 'VAT (5%)' },
  'report.priceWithVat': { ar: 'السعر شامل القيمة', en: 'Price incl. VAT' },
  'report.planLabel': { ar: 'خطة الدفع', en: 'Payment plan' },
  'report.cashFinal': { ar: 'السعر النهائي بعد الخصم', en: 'Final price after discount' },
  'report.flexSchedule': { ar: 'جدول الخطة المرنة', en: 'Flex schedule' },
  'report.recoLine1': {
    ar: 'الموقع: قريب من ٣ محاور رئيسية للطلب — جامعة، مستشفى، واحة معرفة',
    en: 'Location: minutes from 3 demand anchors — university, hospital, knowledge oasis',
  },
  'report.recoYieldStrong': {
    ar: 'العائد الصافي {pct}٪ أعلى بكثير من متوسط السوق العماني (٦–٨٪)',
    en: 'Net yield of {pct}% is significantly above the Oman residential benchmark (6–8%)',
  },
  'report.recoYieldOK': {
    ar: 'العائد الصافي {pct}٪ ضمن النطاق الجيد للسوق العماني (٦–٨٪)',
    en: 'Net yield of {pct}% sits within the Oman residential benchmark range (6–8%)',
  },
  'report.recoYieldLow': {
    ar: 'العائد الصافي {pct}٪ — تحفظي، لكن مناسب للاحتفاظ طويل الأمد',
    en: 'Net yield of {pct}% is conservative — suitable for long-term hold',
  },
  'report.recoPaybackShort': {
    ar: 'فترة الاسترداد {yrs} سنة — أفق استثماري معقول',
    en: 'Payback in {yrs} years — a reasonable investment horizon',
  },
  'report.recoPaybackLong': {
    ar: 'فترة الاسترداد {yrs} سنة — يناسب من يستهدف الاستثمار طويل الأمد',
    en: 'Payback in {yrs} years — suits long-term investors',
  },
  'report.recoProtect': {
    ar: 'الحماية: ٥ ضمانات قانونية تشمل حساب ضمان بنكي وضمان البناء وحماية من التأخير',
    en: 'Protection: 5 legal guarantees including bank escrow, construction warranty, and delay protection',
  },
  'report.recoGoalLine': {
    ar: 'هدفك من الشراء: {goal}',
    en: 'Your purchase goal: {goal}',
  },
  'report.marketBeds': { ar: 'غرف', en: 'beds' },
  'report.marketSource': { ar: 'المصدر', en: 'Source' },
  'report.marketArea': { ar: 'المساحة', en: 'Area' },
  'report.marketPrice': { ar: 'السعر', en: 'Price' },
  'report.marketPpsqm': { ar: 'السعر/م²', en: 'Price/sqm' },
  'report.marketDate': { ar: 'النشر', en: 'Listed' },
  'report.marketAvgLine': {
    ar: 'متوسط السوق لهذا النوع: {avg} ر.ع — وحدتك أرخص بـ {pct}٪',
    en: 'Market average for this type: OMR {avg} — your unit is {pct}% cheaper',
  },
  'report.marketAvgLineHigher': {
    ar: 'متوسط السوق لهذا النوع: {avg} ر.ع — وحدتك أعلى بـ {pct}٪',
    en: 'Market average for this type: OMR {avg} — your unit is {pct}% more expensive',
  },
  'report.marketEmpty': {
    ar: 'لا توجد مقارنات سوقية بنفس عدد الغرف',
    en: 'No market comparables for this bedroom count',
  },
  'report.amenityCategory': { ar: 'فئة', en: 'Category' },
  'report.tableUnit': { ar: 'الوحدة', en: 'Unit' },
  'report.tableType': { ar: 'النوع', en: 'Type' },
  'report.tableFloor': { ar: 'الطابق', en: 'Floor' },
  'report.tableArea': { ar: 'م²', en: 'sqm' },
  'report.tableView': { ar: 'الإطلالة', en: 'View' },
  'report.tableBase': { ar: 'سعر أساسي', en: 'Base' },
  'report.tableTotal': { ar: 'مع القيمة', en: 'incl. VAT' },
  'report.tableMonthly': { ar: 'القسط الشهري', en: 'Monthly' },
  'report.tableNetYield': { ar: 'العائد الصافي', en: 'Net yield' },
  'report.disclaimer': {
    ar: 'هذا التقرير لأغراض الاطلاع فقط ولا يشكل عرضاً ملزماً. الأسعار والأرقام قابلة للتغيير حتى توقيع العقد. يخضع الحجز والدفع لشروط شركة المفتاح العقارية وحساب الضمان البنكي.',
    en: 'This report is informational only and does not constitute a binding offer. Prices and figures may change until contract signing. Booking and payment are subject to Al Moftah Real Estate terms and bank escrow conditions.',
  },
  'report.brand': { ar: 'المفتاح العقارية', en: 'Al Moftah Real Estate' },
  'report.generatedAt': { ar: 'أُنشئ في', en: 'Generated at' },

  // Sections (priority order)
  'section.project': { ar: 'قوة المشروع والموقع', en: 'Project strength & location' },
  'section.numbers': { ar: 'الأرقام والعائد', en: 'Numbers & yield' },
  'section.guarantees': { ar: 'الضمانات', en: 'Guarantees' },
  'section.market': { ar: 'مقارنة السوق', en: 'Market comparison' },

  // Phase 8f-bcs (2026-05-07) — 3-scenario yield panel + status badges
  'yield.low':           { ar: 'إيجار أدنى',  en: 'Low rent' },
  'yield.mid':           { ar: 'إيجار متوسط', en: 'Mid rent' },
  'yield.high':          { ar: 'إيجار أعلى',  en: 'High rent' },
  'yield.explainer':     { ar: 'كيف يزيد',    en: 'How to raise' },
  'yield.scenario':      { ar: 'السيناريو',   en: 'Scenario' },
  'yield.rentLabel':     { ar: 'الإيجار',     en: 'Rent' },
  'yield.yieldLabel':    { ar: 'العائد',      en: 'Yield' },
  'yield.paybackLabel':  { ar: 'الاسترداد',   en: 'Payback' },

  'report.investorSummary': { ar: '💼 ملخص للمستثمر', en: '💼 Investor summary' },
  'report.residentSummary': { ar: '🏠 ملخص للساكن',   en: '🏠 Resident summary' },

  'match.statusReserved': { ar: 'محجوز', en: 'Reserved' },
  'match.statusSold':     { ar: 'مباع',  en: 'Sold' },
  'match.floorLabel':     { ar: 'الطابق', en: 'Floor' },
  'match.bedsLabel':      { ar: 'غرف',    en: 'beds' },

  // Phase 8f-bcs B.2 (2026-05-08) — server-rendered PDF download
  'share.downloadPdf': { ar: '📥 تحميل PDF',                            en: '📥 Download PDF' },
  'share.pdfFailed':   { ar: '⚠️ تعذّر توليد PDF — استخدم QR code', en: '⚠️ PDF generation failed — use QR code' },

  // Phase 8f-bcs B.3 (2026-05-08) — WhatsApp send status
  'share.whatsappSent':     { ar: '✅ تم إرسال التقرير عبر واتساب إلى', en: '✅ Report sent via WhatsApp to' },
  'share.whatsappFailed':   { ar: '⚠️ تعذّر الإرسال عبر واتساب',          en: '⚠️ WhatsApp send failed' },
  'share.whatsappFallback': { ar: 'استخدم QR code أو الرابط أدناه',         en: 'Use the QR code or link below' },

  // V3 D4 Phase B (2026-05-09) — V2 welcome page
  // D6.4b — 'v2.welcome.tagline' removed; the welcome card no longer
  // shows a tagline below the KEY2 wordmark.
  'v2.welcome.scrollIndicator':   { ar: 'اسحب للأسفل',                en: 'Scroll down' },
  'v2.welcome.showcaseTitle':     { ar: 'اكتشف KEY2',                 en: 'Discover KEY2' },
  'v2.welcome.formTitle':         { ar: 'ابدأ تجربتك',                 en: 'Start your journey' },
  'v2.welcome.formSubtitle':      { ar: 'أخبرنا عن نفسك لنبدأ',       en: 'Tell us a bit about yourself' },
  'v2.welcome.fieldName':         { ar: 'الاسم الكامل',                en: 'Full name' },
  'v2.welcome.fieldPhone':        { ar: 'رقم الهاتف (اختياري)',       en: 'Phone (optional)' },
  'v2.welcome.fieldAdvisor':      { ar: 'مستشار العقارات',             en: 'Real estate advisor' },
  'v2.welcome.placeholderName':   { ar: 'اكتب اسمك الكامل',            en: 'Enter your full name' },
  'v2.welcome.errorNameRequired': { ar: 'الاسم مطلوب',                 en: 'Name is required' },
  'v2.welcome.errorNameShort':    { ar: 'الاسم قصير جداً',             en: 'Name is too short' },
  'v2.welcome.errorPhoneInvalid': { ar: 'رقم الهاتف يجب أن يكون ٨ أرقام', en: 'Phone must be 8 digits' },
  'v2.welcome.cta':               { ar: 'ابدأ الجولة ←',                en: 'Begin tour →' },

  // V3 Day 3.1 (2026-05-09) — MapExperience modal
  'mapExperience.title':           { ar: '🗺️ استكشف محيط KEY2',          en: '🗺️ Explore the KEY2 Surroundings' },
  'mapExperience.openButton':      { ar: '🗺️ استكشف المنطقة',             en: '🗺️ Explore the Area' },
  'mapExperience.openFull':        { ar: 'افتح الخريطة الكاملة',           en: 'Open the full map' },
  'mapExperience.allCategories':   { ar: 'الكل',                            en: 'All' },
  'mapExperience.distance':        { ar: 'المسافة',                         en: 'Distance' },
  'mapExperience.distFromKey2':    { ar: 'المسافة من KEY2',                 en: 'Distance from KEY2' },
  'mapExperience.fromKey2':        { ar: 'من KEY2',                         en: 'from KEY2' },
  'mapExperience.driveTime':       { ar: 'وقت السياقة',                     en: 'Drive time' },
  'mapExperience.driveCircles':    { ar: 'دوائر وقت السياقة',               en: 'Drive-time rings' },
  'mapExperience.openInMaps':      { ar: 'افتح في خرائط Google',           en: 'Open in Google Maps' },
  'mapExperience.demandThemes':    { ar: 'قصص الطلب',                       en: 'Demand stories' },
  'mapExperience.proximity':       { ar: 'مؤشرات القرب الرئيسية',           en: 'Key proximity indicators' },
  'mapExperience.showOnMap':       { ar: '📍 شاهد على الخريطة',             en: '📍 Show on map' },
  'mapExperience.tabMap':          { ar: 'الخريطة',                         en: 'Map' },
  'mapExperience.tabFilters':      { ar: 'الفلاتر',                         en: 'Filters' },
  'mapExperience.tabStories':      { ar: 'قصص الطلب',                       en: 'Stories' },
  'mapExperience.minutes':         { ar: 'دقيقة',                           en: 'min' },
  'mapExperience.km':              { ar: 'كم',                              en: 'km' },
  'mapExperience.selectMarker':    { ar: 'اضغط على معلم لعرض التفاصيل',     en: 'Tap a marker to see details' },
  'mapExperience.insightStatement': {
    ar: 'الطلب على KEY2 لا يعتمد على مصدر واحد. التقاء التعليم والتقنية والصناعة والرعاية الصحية يخلق قاعدة طلب متنوعة تقلل مخاطر الشواغر وتدعم دخلًا إيجاريًا مستمرًا.',
    en: "KEY2's demand is not dependent on a single source. The convergence of education, technology, industry, and healthcare creates a diversified demand base that reduces vacancy risk and supports consistent rental income.",
  },

  // 11 category filter chips (10 BCS categories + meta "All")
  'mapCat.education':    { ar: 'تعليم',     en: 'Education' },
  'mapCat.healthcare':   { ar: 'صحة',       en: 'Health' },
  'mapCat.tech':         { ar: 'تقنية',     en: 'Technology' },
  'mapCat.shopping':     { ar: 'تسوق',      en: 'Shopping' },
  'mapCat.transport':    { ar: 'نقل',       en: 'Transport' },
  'mapCat.services':     { ar: 'خدمات',    en: 'Services' },
  'mapCat.landmark':     { ar: 'معالم',    en: 'Landmarks' },
  'mapCat.industry':     { ar: 'صناعة',    en: 'Industry' },
  'mapCat.road':         { ar: 'طرق',      en: 'Roads' },
  'mapCat.neighborhood': { ar: 'أحياء',    en: 'Districts' },

  // V3 Day 2.5 (2026-05-08) — Welcome Splash (Step 0)
  'welcome.tagline':      { ar: 'رمز عيشتك الجديدة',         en: 'Key to your new lifestyle' },
  'welcome.watchProject': { ar: 'شاهد المشروع',                en: 'Watch the project' },
  'welcome.startConfig':  { ar: 'ابدأ التخصيص',                en: 'Start configuring' },
  'welcome.brand':        { ar: 'المفتاح العقارية',           en: 'Al Moftah Real Estate' },
  'welcome.backHome':     { ar: '← العودة للبداية',           en: '← Back home' },

  // V3 Day 2 (2026-05-08) — Cinematic ProjectShowcase modal
  'showcase.discoverKey2': { ar: '🏛️ اكتشف KEY2',     en: '🏛️ Discover KEY2' },
  'showcase.title':        { ar: 'جولة في برج KEY2',   en: 'KEY2 Tower Tour' },
  'showcase.play':         { ar: 'تشغيل',              en: 'Play' },
  'showcase.pause':        { ar: 'إيقاف',              en: 'Pause' },
  'showcase.next':         { ar: 'التالي',             en: 'Next' },
  'showcase.previous':     { ar: 'السابق',             en: 'Previous' },
  'showcase.fullscreen':   { ar: 'ملء الشاشة',        en: 'Fullscreen' },
  'showcase.close':        { ar: 'إغلاق',              en: 'Close' },
  'showcase.audioOn':      { ar: 'الصوت',              en: 'Audio' },
  'showcase.audioOff':     { ar: 'كتم',                 en: 'Mute' },
  'showcase.loading':      { ar: 'جارٍ التحميل…',     en: 'Loading…' },
  'showcase.imageError':   { ar: 'تعذّر تحميل الصورة', en: 'Image failed to load' },
};

export function useT() {
  const lang = useSessionStore((s) => s.language);
  return (key: keyof typeof strings) => strings[key]?.[lang] ?? key;
}

/** Static accessor for use outside React components. */
export function t(key: keyof typeof strings, lang: 'ar' | 'en' = 'ar'): string {
  return strings[key]?.[lang] ?? key;
}

export type StringKey = keyof typeof strings;
